import { Button, Checkbox, Dropdown, TDropDownHandle } from "@r360/library";
import classNames from "classnames";
import { sum } from "lodash";
import moment from "moment-timezone";
import queryString from "query-string";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { RootState } from "../..";
import AxiosClient from "../../API/AxiosClient";
import { productTypeNames } from "../../Constants";
import { getPathWithoutBasename } from "../../Helper";
import useAppDispatch from "../../hooks/useAppDispatch";
import useAppSelector from "../../hooks/useAppSelector";
import { usePreFetch } from "../../hooks/usePreFetch";
import useTranslate from "../../hooks/useTranslate";
import { requestLoadingByResvTypeSelector } from "../../Selectors";
import { SkipassProductSelector } from "../../selectors/productsSelectors";
import { setRequestsLoadingByResvType } from "../../store/actions/axiosStatus";
import { setPreFetchUrl, setPromoCodeError } from "../../store/actions/reservationResult";
import { TReservationResult, TReservationType } from "../../store/types";
import { GuestsPopover } from "../Guests/Guests";
import { PromotionCode } from "../PromotionCode";
import { ResetSearchButton } from "../ResetSearchButton";
import DatePicker from "../UI/DatePicker";
import { TAvailablePeriod, TBlockedPeriod } from "../UI/DatePicker/DatePicker";
import "./MenuSearch.scss";
import { ErrorBoundary } from "../ErrorBoundary";

type TMenuSearch = {
    reservationTypes: TReservationType[];
    onDarkBackground?: boolean;
    availablePeriods?: { [key: string]: TAvailablePeriod[] | undefined };
    blockedPeriods?: { [key: string]: TBlockedPeriod[] | undefined };
    searchButtonText?: string;
    showMenuSearch?: boolean;
    outsideHeader?: boolean;
    onSelectStartDate?: (startDate: string) => void;
    onSelectDates?: (startDate: string, endDate: string) => void;
    maxRangeLength?: number;
    groupPoollid?: number;
    poollid?: number;
    unitlid?: number;
    isSmall?: boolean;
};

enum TCalendarType {
    SINGLE_CALENDAR = "single",
    DOUBLE_CALENDAR = "double",
}

type TSearchFields = {
    calendarType?: TCalendarType;
    calendarTogglable?: boolean;
    numberOfDays?: boolean;
    guests?: boolean;
    withSearchButton?: boolean;
    discountCode?: boolean;
    productSelector?: (state: RootState, reservationTypeid: number) => [];
};

export type TAge = {
    description: string;
    key: string;
    max: number;
    min: number;
    minCount?: number;
    title: string;
    value: number;
    requiresAge?: boolean;
    default?: number;
};

export type TAgesAreValid = {
    [key: string]: boolean;
};

type TErrors = {
    arrivalDate: boolean;
    departureDay?: boolean;
    guests?: boolean;
};

export const MenuSearch: React.FC<TMenuSearch> = ({
    reservationTypes,
    onDarkBackground = true,
    availablePeriods,
    blockedPeriods,
    searchButtonText,
    showMenuSearch = true,
    // outsideHeader = false,
    onSelectStartDate,
    onSelectDates,
    maxRangeLength,
    groupPoollid,
    poollid,
    unitlid,
    isSmall,
}) => {
    const t = useTranslate();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const activeReservationType = reservationTypes.find(({ current }) => current);
    const reservationTypeResult = useAppSelector(
        state => activeReservationType && state.reservationResult[activeReservationType.id]
    ) as TReservationResult | undefined;
    const clientData = useAppSelector((state: RootState) => state.clientData);

    const guestData = clientData?.options?.general;
    const [selectedStartDate, setSelectedStartDate] = useState("");
    const [selectedEndDate, setSelectedEndDate] = useState("");
    const { useCampaign, allowOneDayOnly } = (
        activeReservationType?.id
            ? clientData.reservationtypes.find((x: TReservationType) => x.id === activeReservationType.id)
            : {}
    ) as TReservationType;
    const [guestsField, setGuestsField] = useState<{ [key: string]: number }>({});
    const [allowSearch, setAllowSearch] = useState(false);
    const [calendarType, setCalendarType] = useState<TCalendarType>(TCalendarType.DOUBLE_CALENDAR);
    const guestsDropdownRef = useRef<TDropDownHandle>(null);
    const { isMobile } = useSelector((state: RootState) => state.window);
    const [requiredAges, setRequiredAges] = useState<{ [key: string]: (number | string)[] }>({});
    const requiredAgesPrefix = "ages-for-";
    const [enableEndDate, setEnableEndDate] = useState(true);
    const searchResultLoading = useAppSelector(state =>
        requestLoadingByResvTypeSelector(state, "fetchReservationTypeResults", activeReservationType?.id)
    );
    const promoCodeError = reservationTypeResult?.promoCodeError;
    const location = useLocation();
    const prevActiveResvTypeId = useRef<number>();
    const [maxGuests, setMaxGuests] = useState<number | undefined>();
    const [searchBtnClicked, setSearchBtnClicked] = useState(false);
    const { allowPreFetch, preFetchResult, preFetchLoading } = usePreFetch(activeReservationType?.id ?? 0);
    const [searchDataTouched, setSearchDataTouched] = useState(false);
    const filters = useAppSelector(state => state.filter);
    const activeReservationTypeFilters = activeReservationType ? filters[activeReservationType.id] : undefined;
    const [errors, setErrors] = useState<TErrors>({ arrivalDate: false, departureDay: false, guests: false });
    const hasErrors = Object.values(errors).some(error => error);
    const [promotionCode, setPromotionCode] = useState<{ code: string; validated: boolean; reset?: boolean }>({
        code: "",
        validated: false,
        reset: false,
    });
    const [searchingWithPromotionCode, setSearchingWithPromotionCode] = useState(false);
    const [guestDropdownOpen, setGuestDropdownOpen] = useState(false);

    const setSelectedDate = ({ startDate, endDate }: { startDate?: string | null; endDate?: string | null }) => {
        setSelectedStartDate(startDate || "");
        setSelectedEndDate(endDate || "");
    };

    const menuSearchFields: TSearchFields = useMemo(() => {
        switch (activeReservationType?.type) {
            case productTypeNames.SKIPASS: {
                return {
                    calendarType: TCalendarType.SINGLE_CALENDAR,
                    productSelector: SkipassProductSelector,
                    withSearchButton: true,
                    discountCode: useCampaign ?? true,
                };
            }
            case productTypeNames.ACTIVITY: {
                return {
                    calendarType: TCalendarType.DOUBLE_CALENDAR,
                    calendarTogglable: allowOneDayOnly ?? false,
                    discountCode: useCampaign ?? true,
                    withSearchButton: true,
                };
            }
            case productTypeNames.LETTING: {
                return {
                    calendarType: TCalendarType.DOUBLE_CALENDAR,
                    discountCode: useCampaign ?? true,
                    withSearchButton: true,
                    calendarTogglable: allowOneDayOnly ?? false,
                };
            }
            case productTypeNames.RENTAL: {
                return {
                    calendarType: TCalendarType.DOUBLE_CALENDAR,
                    calendarTogglable: allowOneDayOnly ?? false,
                    discountCode: useCampaign ?? true,
                    withSearchButton: true,
                };
            }
            case productTypeNames.ACCOMMODATION: {
                return {
                    calendarType: TCalendarType.DOUBLE_CALENDAR,
                    guests: true,
                    withSearchButton: true,
                    discountCode: useCampaign ?? true,
                };
            }
            case productTypeNames.PACKAGE: {
                return {
                    calendarType: TCalendarType.DOUBLE_CALENDAR,
                    guests: true,
                    withSearchButton: true,
                };
            }

            default: {
                return {};
            }
        }
    }, [activeReservationType?.type, useCampaign, allowOneDayOnly]);

    const getSearchButtonText = (): string => {
        if (searchButtonText) {
            return searchButtonText;
        }

        switch (activeReservationType?.type) {
            case productTypeNames.SKIPASS: {
                return t("book.search.skipass");
            }
            case productTypeNames.ACTIVITY: {
                return t("book.search.activity");
            }
            case productTypeNames.LETTING: {
                return t("book.search.letting");
            }
            case productTypeNames.RENTAL: {
                return t("book.search.rental");
            }
            case productTypeNames.ACCOMMODATION: {
                return t("book.search.accommodation");
            }

            default: {
                return t("book.general.search");
            }
        }
    };

    // Reset certain states when the active reservation type changes.
    useEffect(() => {
        // If the active reservation type has changed, cancel the loading state for the previous reservation type.
        dispatch(
            setRequestsLoadingByResvType({
                request: "fetchReservationTypeResults",
                resvTypeId: prevActiveResvTypeId.current,
                loading: false,
            })
        );

        setSearchDataTouched(false);
        setSearchBtnClicked(false);
        prevActiveResvTypeId.current = activeReservationType?.id;
    }, [activeReservationType?.id, prevActiveResvTypeId, dispatch]);

    useEffect(() => {
        if (allowPreFetch) {
            if (!hasErrors && preFetchLoading && searchBtnClicked) {
                /* If the search button is clicked while the preFetch is loading, set the loading state to true so that skeleton screens are shown. */

                dispatch(
                    setRequestsLoadingByResvType({
                        request: "fetchReservationTypeResults",
                        resvTypeId: activeReservationType?.id,
                        loading: true,
                    })
                );
            } else if (!hasErrors && !preFetchLoading && preFetchResult?.search && searchBtnClicked) {
                /*
                    We can end up here in two scenarios:

                    1. The search button was clicked while the preFetch was loading, and now the preFetch is done.
                    2. The preFetch was finished, and the search button was clicked.

                    In the second scenario, we must show the skeletons for a brief moment; otherwise, the user won't understand that a search has been made. Therefore, we trigger the loading state and quickly turn it off again. This approach works for both scenarios.
                */
                dispatch(
                    setRequestsLoadingByResvType({
                        request: "fetchReservationTypeResults",
                        resvTypeId: activeReservationType?.id,
                        loading: true,
                    })
                );

                setTimeout(() => {
                    dispatch(
                        setRequestsLoadingByResvType({
                            request: "fetchReservationTypeResults",
                            resvTypeId: activeReservationType?.id,
                            loading: false,
                        })
                    );
                }, 200);

                setSearchBtnClicked(false);
            } else {
                setSearchBtnClicked(false);
            }
        }
        // eslint-disable-next-line
    }, [preFetchLoading, searchBtnClicked, dispatch, allowPreFetch, hasErrors]);

    useEffect(() => {
        menuSearchFields?.calendarType &&
            setInitialDatesData(menuSearchFields?.calendarType, menuSearchFields.calendarTogglable || false);
        setErrors({ arrivalDate: false, departureDay: false, guests: false });
    }, [
        activeReservationType?.id,
        menuSearchFields,
        reservationTypeResult?.search?.arrdate,
        reservationTypeResult?.search?.depdate,
        location.search,
    ]);

    useEffect(() => {
        menuSearchFields?.guests && setInitialGuestsData(true);
        // eslint-disable-next-line
    }, [activeReservationType?.id, menuSearchFields, reservationTypeResult?.search?.guests]);

    useEffect(() => {
        setInitialPromotionCodeData();
        // eslint-disable-next-line
    }, [activeReservationType?.id, reservationTypeResult?.search?.promoCode]);

    useEffect(() => {
        setAllowSearch(checkIsAllowed());
        // eslint-disable-next-line
    }, [activeReservationType?.id, selectedStartDate, selectedEndDate, guestsField, requiredAges, menuSearchFields]);

    // Trigger pre-fetch when search dates are changed.
    useEffect(() => {
        if (allowSearch && searchDataTouched && !searchingWithPromotionCode && !guestDropdownOpen) {
            if (calendarType === TCalendarType.SINGLE_CALENDAR) {
                selectedStartDate && triggerPreFetch();
            }

            if (calendarType === TCalendarType.DOUBLE_CALENDAR) {
                selectedStartDate && selectedEndDate && triggerPreFetch();
            }
        }
        // eslint-disable-next-line
    }, [selectedStartDate, selectedEndDate, allowSearch, searchDataTouched]);

    /**
     * Get the max guest count for the active reservation type only if it's of type accommodation.
     */
    useEffect(() => {
        // Don't allow the max guest count to be set if no search has been made, or a search with price has been made. (only allow for for searches without price)
        // Otherwise, the max guest count will be set based on the search result,
        // which may wrongly limit the guest count for other dates.
        if (!reservationTypeResult?.search || reservationTypeResult.search.checkPriceAndAvailability) {
            if (maxGuests !== undefined) {
                setMaxGuests(undefined);
            }

            return;
        }

        // If active reservation type is of type accommodation, get the max guest count.
        if (activeReservationType?.type === productTypeNames.ACCOMMODATION) {
            // Flat map all units and get the max guest count.
            const allItems = Object.values(reservationTypeResult?.groups || {})
                .flatMap(group => group.items)
                .filter(
                    item =>
                        (!groupPoollid || item.grouppoollid === groupPoollid) &&
                        (!poollid || item.poollid === poollid) &&
                        (!unitlid || item.unitlid === unitlid)
                );

            const maxCapac = Math.max(...allItems.map(item => item.capac || 0), 0);

            setMaxGuests(maxCapac || undefined);
        } else if (activeReservationType?.type === productTypeNames.PACKAGE) {
            // For packages, the capac is on the group level.
            const allPackageGroups = Object.values(reservationTypeResult?.groups || {});
            const maxCapac = Math.max(...allPackageGroups.map(item => item.capac || 0), 0);

            setMaxGuests(maxCapac || undefined);
        } else {
            setMaxGuests(undefined);
        }
    }, [activeReservationType, reservationTypeResult, groupPoollid, poollid, unitlid, maxGuests]);

    const guestCount = sum(Object.values(guestsField));

    const checkIsAllowed = () => {
        if (calendarType === TCalendarType.SINGLE_CALENDAR) {
            if (!moment(selectedStartDate).isValid()) return false;
        }

        if (calendarType === TCalendarType.DOUBLE_CALENDAR) {
            if (!moment(selectedStartDate).isValid() || !moment(selectedEndDate).isValid()) return false;
        }

        if (menuSearchFields?.guests) {
            if (guestCount <= 0) {
                return false;
            }

            // If a guest that requires age have an undefined age
            if (Object.values(requiredAges).flat().includes("undefined")) {
                return false;
            }
        }

        return true;
    };

    const setInitialDatesData = (calendarType: TCalendarType, calendarTogglable: boolean) => {
        //Only set dates if it's not the initial automatic search

        const queryStringValues = queryString.parse(location.search);
        const arrdate = queryStringValues.startDate || reservationTypeResult?.search?.arrdate;
        const depdate = queryStringValues.endDate || reservationTypeResult?.search?.depdate;

        if (
            reservationTypeResult?.search?.checkPriceAndAvailability &&
            arrdate &&
            depdate &&
            // Never expects an array, but the type of queryStringValues can support it.
            !Array.isArray(arrdate) &&
            !Array.isArray(depdate)
        ) {
            setEnableEndDate(true);
            setSelectedDate({
                startDate: arrdate,
                endDate: depdate,
            });
        } else if (
            !reservationTypeResult?.search?.checkPriceAndAvailability &&
            queryStringValues.startDate &&
            queryStringValues.endDate
        ) {
            setEnableEndDate(true);
            setSelectedDate({
                startDate: queryStringValues.startDate as string,
                endDate: queryStringValues.endDate as string,
            });
        } else {
            setSelectedDate({});
            setEnableEndDate(false);
        }

        if (reservationTypeResult?.groups && calendarTogglable) {
            if (arrdate === depdate) {
                setCalendarType(TCalendarType.SINGLE_CALENDAR);
            } else {
                setCalendarType(TCalendarType.DOUBLE_CALENDAR);
            }
        } else {
            setCalendarType(calendarType);
        }
    };

    const setInitialGuestsData = (useParams = false) => {
        if (guestData?.ages && activeReservationType) {
            const obj = JSON.parse(guestData?.ages);
            const ages = obj[activeReservationType.id];
            const guests: { [key: string]: number } = {};
            const requiredAgesFromStore: { [key: string]: (number | string)[] } = {};
            const totalGuestCount = useParams ? (reservationTypeResult?.search?.guests || []).length : 0;

            ages?.forEach((age: TAge) => {
                const guestCountFromStore = (reservationTypeResult?.search?.guests || [])?.filter(
                    (guestAge: number) => guestAge >= age.min && guestAge <= age.max
                ).length;

                const guestCount = useParams && guestCountFromStore ? guestCountFromStore : 0;

                if (totalGuestCount === 0 && age.default && age.default > (age.minCount || 0)) {
                    guests[age.key] = guestCount ? guestCount : age.default;
                } else if (age.minCount || (age.default && age.minCount && age.default < age?.minCount)) {
                    guests[age.key] = age.minCount > guestCount ? age.minCount : guestCount;
                } else {
                    guests[age.key] = guestCount ? guestCount : 0;
                }

                if (age.requiresAge) {
                    requiredAgesFromStore[age.key] =
                        reservationTypeResult?.search?.guests?.filter(
                            (guestAge: number) => guestAge >= age.min && guestAge <= age.max
                        ) || [];
                }
            });

            setGuestsField(guests);
            setRequiredAges(requiredAgesFromStore);
        }
    };

    const handleDateChange = (startDate: string, endDate: string | null = null) => {
        setErrors({ arrivalDate: false, departureDay: false, guests: false });
        if (!startDate && !endDate) {
            setEnableEndDate(false);
        }

        if (moment(startDate).isValid() && !enableEndDate) {
            setEnableEndDate(true);
        }

        if (calendarType === TCalendarType.DOUBLE_CALENDAR) {
            if (moment(startDate).isValid() && !moment(endDate).isValid()) {
                setSelectedDate({
                    startDate,
                    endDate: null,
                });
                onSelectStartDate?.(startDate);
            } else if (moment(startDate).isValid() && moment(endDate).isValid()) {
                onSelectDates?.(startDate, endDate as string);
                setEnableEndDate(true);
                setSelectedDate({
                    startDate,
                    endDate,
                });
            }
        } else if (calendarType === TCalendarType.SINGLE_CALENDAR) {
            if (moment(startDate).isValid()) {
                setSelectedDate({
                    startDate,
                    endDate: startDate,
                });
            }
        }

        setSearchDataTouched(true);
    };

    const handleGuestChange = (count: number, data: TAge) => {
        setErrors({ ...errors, guests: false });
        if (count < 1) {
            setGuestsField({ ...guestsField, [data.key]: 0 });
        } else {
            setGuestsField({ ...guestsField, [data.key]: count });
        }
    };

    const createSearchUrl = () => {
        let queryStringObject: { [key: string]: string | number | null } = {
            startDate: null,
            endDate: null,
        };

        queryStringObject.startDate = selectedStartDate;
        queryStringObject.endDate = selectedEndDate;

        if (menuSearchFields?.guests) {
            for (const [key, value] of Object.entries(requiredAges)) {
                if (value.length > 0) {
                    queryStringObject[`${requiredAgesPrefix}${key}`] = value?.join().replaceAll(",", "-");
                }
            }

            queryStringObject = { ...queryStringObject, ...guestsField };
        }

        // Add active filters to the query string.
        if (activeReservationTypeFilters) {
            queryStringObject.filters = btoa(JSON.stringify(activeReservationTypeFilters));
        }

        // Add promotion code to the query string.
        if (promotionCode.code) {
            queryStringObject.promotionCode = promotionCode.code;
        }

        return queryString.stringify(queryStringObject);
    };

    const handleSearch = async (triggeredBySearchBtn = false) => {
        if (triggeredBySearchBtn) {
            setSearchBtnClicked(triggeredBySearchBtn);
        }

        const newErrors = { ...errors };

        if (!selectedStartDate) {
            newErrors.arrivalDate = true;
        }

        if (!selectedEndDate) {
            newErrors.departureDay = true;
        }

        if (Object.values(guestsField).every(item => item === 0)) {
            newErrors.guests = true;
        }

        setErrors(newErrors);
        const pathname = getPathWithoutBasename(window.location.pathname);
        const url = createSearchUrl();

        if (checkIsAllowed()) {
            navigate(`${pathname}?${url}`, {
                replace: true,
                state: { scrollToTop: false, resetPromoCode: promotionCode.reset },
            });

            // If we searched with a promotion code, validate it so it can be used once the search errors are fixed.
        } else if (promotionCode.code && searchingWithPromotionCode) {
            await validatePromotionCode(promotionCode.code);
            setSearchingWithPromotionCode(false);
        } else if (promotionCode.reset && searchingWithPromotionCode) {
            setPromotionCode({ code: "", validated: false, reset: false });
            setSearchingWithPromotionCode(false);
        }
    };

    const validatePromotionCode = async (code: string) => {
        try {
            const response = await AxiosClient.post("/promotions", { promoCode: code });

            if (response) {
                setPromotionCode({ code: response.data.payload.promotionCode, validated: true });
                dispatch(setPromoCodeError(activeReservationType?.id, false));
            }
        } catch (error) {
            dispatch(setPromoCodeError(activeReservationType?.id, true));
        }
    };

    const datePickerClassName = classNames(
        calendarType === TCalendarType.SINGLE_CALENDAR
            ? "menu-search-fields__dates-single"
            : "menu-search-fields__dates"
    );

    const closeGuests = () => {
        if (guestsDropdownRef.current) {
            // start() has type inferrence here as well
            guestsDropdownRef.current.setRefIsOpen();
        }
    };

    const triggerPreFetch = () => {
        // Only do pre-fetch if:
        // - search is allowed
        // - pre-fetch is allowed for the reservation type
        // - search data has been touched

        if (!allowSearch || !allowPreFetch || !searchDataTouched) {
            return;
        }

        const pathname = getPathWithoutBasename(window.location.pathname);
        const url = createSearchUrl();
        dispatch(setPreFetchUrl(`${pathname}?${url}`, activeReservationType?.id));
    };

    const onSingleDateToggle = () => {
        setErrors({ arrivalDate: false, departureDay: false, guests: false });
        setSelectedDate({
            startDate: calendarType === TCalendarType.DOUBLE_CALENDAR ? selectedStartDate : "",
            endDate: calendarType === TCalendarType.DOUBLE_CALENDAR ? selectedStartDate : "",
        });
        setCalendarType(
            calendarType === TCalendarType.DOUBLE_CALENDAR
                ? TCalendarType.SINGLE_CALENDAR
                : TCalendarType.DOUBLE_CALENDAR
        );
    };

    const handleGuestsDropdownClose = () => {
        setGuestDropdownOpen(false);
        setSearchDataTouched(true);
        triggerPreFetch();
    };

    const handleDatePickerClose = () => {
        setSearchDataTouched(true);
    };

    const handleGuestDropdownOpen = () => {
        setGuestDropdownOpen(true);
    };

    useEffect(() => {
        if (searchingWithPromotionCode) {
            handleSearch();
        }
        // eslint-disable-next-line
    }, [promotionCode.code, searchingWithPromotionCode]);

    useEffect(() => {
        if (promoCodeError) {
            setInitialPromotionCodeData();
        }
        // eslint-disable-next-line
    }, [promoCodeError]);

    const setInitialPromotionCodeData = () => {
        const promoCodeFromSearch = reservationTypeResult?.search?.promoCode;

        searchingWithPromotionCode && setSearchingWithPromotionCode(false);

        if (promoCodeFromSearch) {
            setPromotionCode({ code: promoCodeFromSearch, validated: true });
        } else {
            setPromotionCode({ code: "", validated: false });
        }
    };

    const handlePromotionCodeSave = (code: string) => {
        if (promoCodeError) {
            dispatch(setPromoCodeError(activeReservationType?.id, false));
        }

        setSearchDataTouched(true);
        setPromotionCode({
            code,
            validated: false,
            reset: !code,
        });

        // Setting this to true will trigger a useEffect.
        setSearchingWithPromotionCode(true);
    };

    return showMenuSearch ? (
        <div className={isSmall ? "menu-search-small" : "menu-search"}>
            <div className={isSmall ? "menu-search-small-fields" : "menu-search-fields"}>
                {menuSearchFields?.calendarType ? (
                    <div className={isSmall ? "menu-search-small-fields__dates" : datePickerClassName}>
                        <div
                            className={classNames("date-picker-new", {
                                "date-picker-new--disable-end-date": !enableEndDate,
                            })}
                        >
                            <DatePicker
                                key={`${activeReservationType?.id}${groupPoollid ? groupPoollid : ""}${
                                    unitlid ? unitlid : ""
                                }`}
                                resvTypeLid={`${activeReservationType?.id}`}
                                resvTypeType={activeReservationType?.type}
                                showRange={calendarType === TCalendarType.DOUBLE_CALENDAR}
                                singleDate={calendarType === TCalendarType.SINGLE_CALENDAR}
                                onCloseCallback={handleDatePickerClose}
                                changedDate={handleDateChange}
                                startDate={selectedStartDate ? moment(selectedStartDate) : null}
                                endDate={selectedEndDate ? moment(selectedEndDate) : null}
                                startDatePlaceholderText={t("book.search.start_date")}
                                endDatePlaceholderText={t("book.search.end_date")}
                                keepOpenOnDateSelect={false}
                                availablePeriods={availablePeriods}
                                blockedPeriods={blockedPeriods}
                                maxRangeLength={maxRangeLength}
                                hasErrorStart={errors.arrivalDate}
                                hasErrorEnd={errors.departureDay}
                            />
                        </div>
                        {!isMobile && menuSearchFields?.calendarTogglable ? (
                            <Checkbox
                                label={t("book.search.single_date")}
                                checked={calendarType === TCalendarType.SINGLE_CALENDAR}
                                customColorsReversed
                                onChange={onSingleDateToggle}
                            />
                        ) : null}
                    </div>
                ) : null}
                {menuSearchFields?.guests ? (
                    <ErrorBoundary>
                        <div
                            className={classNames({
                                "menu-search-small-fields__guests": isSmall,
                                "menu-search-fields__guests": !isSmall,
                                "menu-search-fields__guests-error": errors.guests,
                            })}
                        >
                            <Dropdown
                                fullWidth
                                label={t("book.search.select_guests")}
                                rightAlign
                                onCloseCallback={handleGuestsDropdownClose}
                                allowCustomColors={onDarkBackground}
                                onOpenCallback={handleGuestDropdownOpen}
                                ref={guestsDropdownRef}
                                value={
                                    guestCount === 0
                                        ? t("book.general.guest")
                                        : guestCount <= 1
                                        ? `${guestCount} ${t("book.general.guest")}`
                                        : `${guestCount} ${t("book.general.guests")}`
                                }
                                isValid={!Object.values(requiredAges).flat().includes("undefined")}
                                onDarkBackground={onDarkBackground}
                            >
                                <GuestsPopover
                                    resvType={`${activeReservationType?.id}`}
                                    filters={guestsField}
                                    onChange={handleGuestChange}
                                    onReset={setInitialGuestsData}
                                    onDone={closeGuests}
                                    requiredAges={requiredAges}
                                    setRequiredAges={setRequiredAges}
                                    maxCount={maxGuests}
                                />
                            </Dropdown>
                        </div>
                    </ErrorBoundary>
                ) : null}
                {menuSearchFields?.withSearchButton ? (
                    <>
                        <Button
                            type="secondary"
                            buttonSize="medium"
                            buttonClassName={
                                isSmall
                                    ? "menu-search-small-fields__search-button"
                                    : "menu-search-fields__search-button"
                            }
                            loading={searchResultLoading || (searchBtnClicked && preFetchLoading)}
                            disabled={searchResultLoading}
                            fullWidth={isMobile || isSmall}
                            onClick={() => handleSearch(true)}
                        >
                            {getSearchButtonText()}
                        </Button>
                        {isMobile && menuSearchFields?.calendarTogglable ? (
                            <div className="menu-search-fields__mobile-calendar-toggle">
                                <Checkbox
                                    label={t("book.search.single_date")}
                                    checked={calendarType === TCalendarType.SINGLE_CALENDAR}
                                    onClick={onSingleDateToggle}
                                    onDarkBackground
                                />
                            </div>
                        ) : null}
                    </>
                ) : null}
                {menuSearchFields?.discountCode ? (
                    <div className="menu-search-fields__discount-code-wrapper">
                        <PromotionCode
                            promotionCode={promotionCode.validated ? promotionCode.code : ""}
                            validating={searchingWithPromotionCode}
                            promotionCodeError={promoCodeError}
                            className="menu-search-fields__discount-code"
                            onDarkBackground={onDarkBackground}
                            buttonClassName={isSmall ? "menu-search-small-fields__discount-button" : ""}
                            onCodeSave={handlePromotionCodeSave}
                        />
                    </div>
                ) : null}
            </div>
            <ResetSearchButton
                activeReservationTypeId={activeReservationType?.id}
                onDarkBackground={onDarkBackground}
            />
        </div>
    ) : menuSearchFields?.discountCode ? (
        <PromotionCode
            promotionCode={promotionCode.validated ? promotionCode.code : ""}
            validating={searchingWithPromotionCode}
            promotionCodeError={promoCodeError}
            buttonClassName="menu-search-fields__discount-code-button"
            onDarkBackground={onDarkBackground}
            withoutMenuSearch
            onCodeSave={handlePromotionCodeSave}
        />
    ) : null;
};
